import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDateDMY, formatRangeDMY, formatMinutesToHM } from '../ui/format';
import DatePicker from '../ui/DatePicker.jsx';
import SegmentedControl from '../components/SegmentedControl.jsx';
import { API_BASE_URL, fetchTeamLoad, fetchUserWorkload, fetchProjectsList, fetchProjectOverview, fetchProjectWorkItems, fetchProjectJobs, fetchJobTasks, createProjectAssignment, fetchPlanningLookups, updateProject, createProject, fetchCalendar, fetchAssignment, updateAssignment, fetchCalendarPeople, fetchDayDetails, fetchManagerClients } from '../api';
import { getActiveTheme, isRedFocusThemeName, radii, resolveThemeName, shadows, spacing, typography, design } from '../ui/tokens';
import Card from '../ui/Card.jsx';
import SectionHeader from '../ui/SectionHeader.jsx';
import Calendar2 from '../components/manager/Calendar2.jsx';
import SharedAddWorkModal from '../components/manager/SharedAddWorkModal.jsx';
import { useAppRefresh } from '../context/AppRefreshContext';
import { deriveProjectColor } from '../utils/projectColors';

const STATUS_COLORS = {
  ok: '#5F7F7A',
  warning: '#9A8F6A',
  overbooked: '#8C5A5A',
  neutral: '#6B7280',
};

const SHOW_RISK_UI = false;

// Deterministic muted palette by project id
function getProjectColor(projectId) {
  const colors = ['#6B7280','#9CA3AF','#818CF8','#60A5FA','#34D399','#F59E0B','#F472B6'];
  const idx = Math.abs(Number(projectId) || 0) % colors.length;
  return colors[idx];
}

// People color mapping (families × 3 shades)
// Brightened families for People calendar (15–25% lighter)
const PEOPLE_FAMILIES = {
  teal: ['#59b6bb', '#4aa7ad', '#3e99a0'],
  amber: ['#c89b53', '#b88943', '#a87833'],
  magenta: ['#b669b5', '#a559a4', '#944a93'],
};
const peopleColorMap = {
  Ivana: { family: 'teal', shade: 1 },
  Josipa: { family: 'teal', shade: 2 },
  Mirna: { family: 'teal', shade: 3 },
  Petar: { family: 'amber', shade: 1 },
  Petra: { family: 'amber', shade: 2 },
  Jana: { family: 'magenta', shade: 1 },
  Lea: { family: 'magenta', shade: 2 },
  Elena: { family: 'magenta', shade: 3 },
};
function getPersonColor(name) {
  const key = String(name || '').trim();
  const meta = peopleColorMap[key];
  if (!meta) return '#9CA3AF';
  const shades = PEOPLE_FAMILIES[meta.family] || PEOPLE_FAMILIES.teal;
  const idx = Math.max(0, Math.min(shades.length - 1, (meta.shade || 1) - 1));
  return shades[idx];
}

// Shared range helper
function toYMD(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

function coerceYmd(v) {
  if (!v) return '';
  const s = String(v).slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : '';
}

function isDoneProject(proj, todayYmd) {
  const endYmd = coerceYmd(proj?.end_date);
  return !!endYmd && endYmd < todayYmd;
}

function getRangeWindow(rangeKey, today = new Date()) {
  const base = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const startOfWeek = (d) => {
    const tmp = new Date(d);
    const dow = tmp.getDay(); // 0=Sun..6=Sat
    const offset = (dow === 0 ? -6 : 1 - dow); // Monday as start
    tmp.setDate(tmp.getDate() + offset);
    return new Date(tmp.getFullYear(), tmp.getMonth(), tmp.getDate());
  };
  const endOfWeek = (d) => {
    const start = startOfWeek(d);
    const tmp = new Date(start);
    tmp.setDate(start.getDate() + 6);
    return new Date(tmp.getFullYear(), tmp.getMonth(), tmp.getDate());
  };
  const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
  const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);

  let fromDate, toDate, label;
  switch (rangeKey) {
    case 'last_week': {
      const b = new Date(base); b.setDate(b.getDate() - 7);
      fromDate = startOfWeek(b); toDate = endOfWeek(b);
      label = formatRangeDMY(fromDate, toDate);
      break;
    }
    case 'this_week': {
      fromDate = startOfWeek(base); toDate = endOfWeek(base);
      label = formatRangeDMY(fromDate, toDate);
      break;
    }
    case 'next_week': {
      const b = new Date(base); b.setDate(b.getDate() + 7);
      fromDate = startOfWeek(b); toDate = endOfWeek(b);
      label = formatRangeDMY(fromDate, toDate);
      break;
    }
    case 'last_month': {
      const b = new Date(base.getFullYear(), base.getMonth() - 1, 1);
      fromDate = startOfMonth(b); toDate = endOfMonth(b);
      label = formatRangeDMY(fromDate, toDate);
      break;
    }
    case 'next_month': {
      const b = new Date(base.getFullYear(), base.getMonth() + 1, 1);
      fromDate = startOfMonth(b); toDate = endOfMonth(b);
      label = formatRangeDMY(fromDate, toDate);
      break;
    }
    case 'this_month':
    default: {
      fromDate = startOfMonth(base); toDate = endOfMonth(base);
      label = formatRangeDMY(fromDate, toDate);
      break;
    }
  }
  return { fromDate, toDate, fromYMD: toYMD(fromDate), toYMD: toYMD(toDate), label };
}

function hexToRgba(hex, alpha) {
  const a = Math.max(0, Math.min(1, Number(alpha)));
  const raw = String(hex || '').trim().replace(/^#/, '');
  const full = raw.length === 3 ? raw.split('').map((c) => c + c).join('') : raw;
  if (!/^[0-9a-fA-F]{6}$/.test(full)) return `rgba(215,38,56,${a})`;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${a})`;
}


function RangeSwitcher({ range, onChange }) {
  const ranges = [
    { key: 'last_week', label: 'Last week' },
    { key: 'this_week', label: 'This week' },
    { key: 'next_week', label: 'Next week' },
    { key: 'last_month', label: 'Last month' },
    { key: 'this_month', label: 'This month' },
  ];
  return (
    <div className="inline-flex rounded overflow-hidden border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800">
      {ranges.map(r => (
        <button
          key={r.key}
          onClick={() => onChange(r.key)}
          className={
            `px-3 py-1 text-sm ${range === r.key ? 'bg-gray-200 dark:bg-gray-700' : ''} hover:bg-gray-200 dark:hover:bg-gray-700`
          }
        >{r.label}</button>
      ))}
    </div>
  );
}

function ManagerFilter({ value, options, onChange }) {
  return (
    <select
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      className="px-2 py-1 text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800"
      title="Manager"
      aria-label="Manager"
    >
      <option value="">All managers</option>
      {(Array.isArray(options) ? options : []).map(m => (
        <option key={m.id} value={String(m.id)}>{m.display_name || '—'}</option>
      ))}
    </select>
  );
}

function ProjectFilter({ projects, managerId, projectId, onChange }) {
  const list = Array.isArray(projects) ? projects : [];
  const filtered = !managerId
    ? list
    : list.filter(p => String(p.created_by_user_id || '') === String(managerId));

  return (
    <select
      value={projectId || ''}
      onChange={e => onChange(e.target.value ? Number(e.target.value) : null)}
      className="px-2 py-1 text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800"
      title="Project"
      aria-label="Project"
    >
      <option value="">All projects</option>
      {filtered
        .slice()
        .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')))
        .map(p => (
          <option key={p.id} value={String(p.id)}>{p.name}</option>
        ))}
    </select>
  );
}

function TeamTile({ user, onClick }) {
  const bg = STATUS_COLORS[user.status] || STATUS_COLORS.neutral;
  const planned = user.planned_minutes_total || 0;
  const logged = user.logged_minutes_total || 0;
  const pct = planned > 0 ? Math.min(100, Math.round((logged * 100) / planned)) : 0;
  const colorClass = planned > 0
    ? (pct < 80 ? 'bg-green-500' : (pct <= 100 ? 'bg-yellow-500' : 'bg-red-500'))
    : 'bg-gray-300';
  return (
    <button
      onClick={onClick}
      className="group relative flex items-center justify-center text-center p-3 hover:brightness-110 transition"
      style={{
        background: bg,
        // Dodecagon shape
        clipPath: 'polygon(30% 5%, 70% 5%, 85% 15%, 95% 30%, 95% 70%, 85% 85%, 70% 95%, 30% 95%, 15% 85%, 5% 70%, 5% 30%, 15% 15%)',
        minHeight: 140,
      }}
      aria-label={`Open workload for ${user.display_name || 'User'}`}
    >
      <div className="text-white">
        <div className="font-semibold text-base mb-1">{user.display_name || 'Unnamed'}</div>
        <div className="text-xs opacity-90">Planned: {formatMinutesToHM(user.planned_minutes_total)}</div>
        <div className="text-xs opacity-90">Logged: {formatMinutesToHM(user.logged_minutes_total)}</div>
        <div className="text-[11px] opacity-80 mt-1">Planned: {formatMinutesToHM(user.logged_assigned_minutes_total || 0)} • Other: {formatMinutesToHM(user.logged_other_minutes_total || 0)}</div>
        <div className="mt-2">
          <div className="h-1.5 bg-white/30 rounded">
            <div className={`h-1.5 ${colorClass} rounded`} style={{ width: `${pct}%` }} />
          </div>
          <div className="text-[10px] mt-0.5 opacity-80">{planned > 0 ? `${pct}% of planned` : 'No planned'}</div>
        </div>
      </div>
    </button>
  );
}

export function PeopleOverview({ title = 'People' }) {
  const [range, setRange] = useState('this_week'); // UI range (can be 'total')
  const [dataRange, setDataRange] = useState('this_week'); // API-supported range key
  const [projectId, setProjectId] = useState(null);
  const [managerId, setManagerId] = useState(() => {
    try { return localStorage.getItem('managerDash.managerId') || ''; } catch { return ''; }
  });
  const [managerOptions, setManagerOptions] = useState([]); // [{id, display_name}]
  const [projectsLite, setProjectsLite] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [team, setTeam] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [projectForWorkload, setProjectForWorkload] = useState(null);

  const isTotal = String(range) === 'total';
  const apiRange = isTotal ? dataRange : range;

  const todayYmd = useMemo(() => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${dd}`;
  }, []);

  const baseWindow = useMemo(() => getRangeWindow(dataRange, new Date()), [dataRange]);
  const window = useMemo(() => {
    if (!isTotal) return getRangeWindow(range, new Date());
    // Total: expand date window based on filtered projects when possible.
    const list = Array.isArray(projectsLite) ? projectsLite : [];
    const scoped = list.filter(p => {
      if (projectId != null && Number(p.id) !== Number(projectId)) return false;
      if (managerId && String(p.created_by_user_id || '') !== String(managerId)) return false;
      return true;
    });

    const validStart = scoped
      .map(p => (p.start_date || '').slice(0, 10))
      .filter(s => /^\d{4}-\d{2}-\d{2}$/.test(String(s)));
    const validEnd = scoped
      .map(p => (p.end_date || '').slice(0, 10))
      .filter(s => /^\d{4}-\d{2}-\d{2}$/.test(String(s)));

    const fallbackFrom = baseWindow.fromYMD;
    const fallbackTo = baseWindow.toYMD;

    const from = validStart.length ? validStart.slice().sort()[0] : fallbackFrom;
    const maxEnd = validEnd.length ? validEnd.slice().sort().slice(-1)[0] : null;
    // Extend to max(project end, today)
    const to = [maxEnd, todayYmd, fallbackTo].filter(Boolean).slice().sort().slice(-1)[0];
    return {
      fromDate: new Date(from + 'T00:00:00'),
      toDate: new Date(to + 'T00:00:00'),
      fromYMD: from,
      toYMD: to,
      label: 'Total',
    };
  }, [isTotal, range, projectsLite, managerId, projectId, baseWindow.fromYMD, baseWindow.toYMD, todayYmd]);

  const from = window.fromYMD;
  const to = window.toYMD;
  const windowLabel = window.label;

  const headerSubtitle = useMemo(() => {
    return windowLabel || (from && to ? `${formatRangeDMY(new Date(from + 'T00:00:00'), new Date(to + 'T00:00:00'))}` : '');
  }, [windowLabel, from, to]);

  const onRangeChange = (nextKey) => {
    const k = String(nextKey || '');
    setRange(k);
    if (k && k !== 'total') setDataRange(k);
  };

  useEffect(() => {
    try { localStorage.setItem('managerDash.managerId', String(managerId || '')); } catch {}
  }, [managerId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const lk = await fetchPlanningLookups({ redirectOn401: false });
        const opts = (Array.isArray(lk?.managers) ? lk.managers : [])
          .slice()
          .sort((a, b) => String(a.display_name || '').localeCompare(String(b.display_name || '')));
        if (!cancelled) setManagerOptions(opts);
      } catch {
        if (!cancelled) setManagerOptions([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true); setError('');
      try {
        const res = await fetchTeamLoad(apiRange, projectId || null, { from, to });
        if (!cancelled && res) {
          setTeam(Array.isArray(res.users) ? res.users : []);
          if (process.env.NODE_ENV !== 'production' && apiRange === 'this_month') {
            const expectedDays = Math.round((new Date(to + 'T00:00:00').getTime() - new Date(from + 'T00:00:00').getTime()) / (1000 * 60 * 60 * 24)) + 1;
            if (expectedDays < 20) {
              console.warn('[PeopleOverview] this_month window seems too short', { from, to, expectedDays, apiFrom: res.from, apiTo: res.to });
            }
          }
        }
      } catch (err) {
        if (!cancelled) setError('Workload data unavailable');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [apiRange, projectId, refreshKey, from, to]);

  return (
    <div className="p-4">
      <SectionHeader
        title={title}
        subtitle={headerSubtitle}
        actions={(
          <>
            <SegmentedControl ariaLabel="Range" options={[{key:'last_week',label:'Last week'},{key:'this_week',label:'This week'},{key:'next_week',label:'Next week'},{key:'last_month',label:'Last month'},{key:'this_month',label:'This month'},{key:'total',label:'Total'}]} value={range} onChange={onRangeChange} />
            <ManagerFilter value={managerId} options={managerOptions} onChange={setManagerId} />
            <ProjectFilter projects={projectsLite} managerId={managerId} projectId={projectId} onChange={setProjectId} />
          </>
        )}
        className="mb-4"
      />

      {loading && (
        <div className="text-sm text-gray-600 dark:text-gray-400">Loading...</div>
      )}
      {error && (
        <div className="text-sm text-red-600">{error}</div>
      )}

      {!loading && !error && (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {team.map(u => (
            <TeamTile key={u.user_id} user={u} onClick={() => { setSelectedUser(u); setProjectForWorkload(null); }} />
          ))}
          {team.length === 0 && (
            <div className="text-sm text-gray-600 dark:text-gray-400">No team data</div>
          )}
        </div>
      )}

      <WorkloadDrawer
        open={!!selectedUser}
        onClose={() => setSelectedUser(null)}
        user={selectedUser}
        range={range}
        from={from}
        to={to}
        projectId={projectForWorkload ? projectForWorkload.id : projectId}
        projectLabel={projectForWorkload ? projectForWorkload.label : undefined}
      />
    </div>
  );
}

function formatDayLabel(ymd) {
  const d = new Date(ymd + 'T00:00:00');
  const days = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
  return `${days[d.getDay()]} ${formatDateDMY(d)}`;
}

function buildDayProjectBreakdown(dayDetails) {
  const plannedRows = dayDetails?.planned_assignments || dayDetails?.planned || [];
  const loggedRows = dayDetails?.logged_entries || dayDetails?.entries || [];
  const groups = new Map();

  function keyFor({ isOther, projectId }) {
    if (isOther) return 'other';
    if (projectId == null) return 'none';
    return `p:${projectId}`;
  }

  function ensureGroup({ key, project }) {
    if (groups.has(key)) return groups.get(key);
    const name = key === 'other'
      ? 'Other'
      : (project?.name || (key === 'none' ? 'No project' : 'Project'));
    const group = {
      key,
      project: project ? { id: project.id ?? null, name: project.name ?? name } : { id: null, name },
      plannedMinutes: 0,
      loggedMinutes: 0,
      plannedItems: [],
      loggedItems: [],
    };
    groups.set(key, group);
    return group;
  }

  function labelForJobTask(job, task) {
    const j = job?.name || '';
    const t = task?.name || '';
    if (j && t) return `${j}     ${t}`;
    return j || t || 'Unlabeled';
  }

  for (const row of plannedRows) {
    const project = row?.project || null;
    const key = keyFor({ isOther: false, projectId: project?.id ?? null });
    const group = ensureGroup({ key, project });
    const minutes = Number(row?.planned_minutes || 0);
    if (minutes <= 0) continue;
    group.plannedMinutes += minutes;
    group.plannedItems.push({
      minutes,
      label: labelForJobTask(row?.job, row?.task),
      assignmentId: row?.assignment_id ?? null,
    });
  }

  for (const row of loggedRows) {
    const isOther = !!row?.is_other;
    const project = isOther ? null : (row?.project || null);
    const key = keyFor({ isOther, projectId: project?.id ?? null });
    const group = ensureGroup({ key, project });
    const minutes = Number(row?.minutes || 0);
    if (minutes <= 0) continue;
    group.loggedMinutes += minutes;
    group.loggedItems.push({
      minutes,
      label: isOther
        ? (row?.description ? `Other     ${row.description}` : 'Other')
        : labelForJobTask(row?.job, row?.task),
      entryId: row?.id ?? null,
      isOther,
    });
  }

  const list = Array.from(groups.values());
  list.sort((a, b) => {
    const rank = (k) => (k === 'other' ? 2 : (k === 'none' ? 1 : 0));
    const ra = rank(a.key);
    const rb = rank(b.key);
    if (ra !== rb) return ra - rb;
    return String(a.project?.name || '').localeCompare(String(b.project?.name || ''));
  });

  return list;
}

function DayCard({ day, userId, projectId }) {
  const planned = day.planned_minutes || 0;
  const logged = day.logged_minutes || 0;
  const cap = 480;
  const plannedPct = Math.min(100, Math.round((planned / cap) * 100));
  const loggedPct = Math.min(100, Math.round((logged / cap) * 100));
  const utilPct = planned > 0 ? Math.min(100, Math.round((logged * 100) / planned)) : 0;
  const utilColor = planned > 0
    ? (utilPct < 80 ? 'bg-green-500' : (utilPct <= 100 ? 'bg-yellow-500' : 'bg-red-500'))
    : 'bg-gray-400';

  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState('');
  const [dayDetails, setDayDetails] = useState(null);

  const breakdown = useMemo(() => buildDayProjectBreakdown(dayDetails), [dayDetails]);

  async function ensureDayDetailsLoaded() {
    if (!userId || !day?.date) return;
    if (dayDetails || detailsLoading) return;
    setDetailsLoading(true);
    setDetailsError('');
    try {
      const res = await fetchDayDetails(userId, day.date, { projectId: projectId || null });
      setDayDetails(res);
    } catch (err) {
      setDetailsError('Failed to load day breakdown');
    } finally {
      setDetailsLoading(false);
    }
  }

  return (
    <div
      className="border border-gray-200 dark:border-gray-700 p-3 bg-white dark:bg-gray-800"
      style={{ borderRadius: radii.sm, boxShadow: shadows.sm }}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="font-medium text-sm">{formatDayLabel(day.date)}</div>
        {planned > cap && (
          <span className="text-xs px-2 py-0.5 rounded bg-red-200/40 text-red-800 dark:bg-red-900/40 dark:text-red-200">Over 8h</span>
        )}
      </div>
      <div className="text-xs mb-2 grid grid-cols-3 gap-2">
        <div>Planned: {formatMinutesToHM(planned)}</div>
        <div>Logged: {formatMinutesToHM(logged)}</div>
        <div>Remaining: {formatMinutesToHM(day.remaining_minutes || 0)}</div>
      </div>
      <div className="text-[11px] text-gray-600 dark:text-gray-300 mb-2">Planned: {formatMinutesToHM(day.logged_assigned_minutes || 0)} • Other: {formatMinutesToHM(day.logged_other_minutes || 0)}</div>
      {/* Project chips (UI-only placeholder) */}
      <div className="flex flex-wrap gap-2 mb-2">
        {logged > 0 ? (
          <span className="text-[11px] px-2 py-1 rounded bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200">Total {formatMinutesToHM(logged)}</span>
        ) : (
          <span className="text-[11px] px-2 py-1 rounded bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400">No logged data</span>
        )}
      </div>
      {/* Accordion placeholder for per-project breakdown */}
      <details
        className="mb-2"
        onToggle={(e) => {
          if (e.currentTarget.open) ensureDayDetailsLoaded();
        }}
      >
        <summary className="text-xs cursor-pointer text-gray-700 dark:text-gray-300">Project breakdown</summary>
        <div className="mt-2 text-xs text-gray-600 dark:text-gray-400">
          {detailsLoading && 'Loading...'}
          {!detailsLoading && detailsError && <span className="text-red-600">{detailsError}</span>}
          {!detailsLoading && !detailsError && breakdown.length === 0 && (
            <span>No planned/logged details</span>
          )}
        </div>
        {!detailsLoading && !detailsError && breakdown.length > 0 && (
          <div className="mt-2 space-y-2">
            {breakdown.map(group => {
              const plannedMins = group.plannedMinutes || 0;
              const loggedMins = group.loggedMinutes || 0;
              const remainingMins = plannedMins - loggedMins;
              const color = group.key === 'other' || group.key === 'none'
                ? '#9CA3AF'
                : getProjectColor(group.project?.id);

              return (
                <div key={group.key} className="border border-gray-200 dark:border-gray-700 rounded p-2">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: color }} />
                      <div className="text-xs font-medium text-gray-800 dark:text-gray-200 truncate">{group.project?.name}</div>
                    </div>
                    <div className="text-[11px] text-gray-600 dark:text-gray-400 whitespace-nowrap">
                      Planned {formatMinutesToHM(plannedMins)}     Logged {formatMinutesToHM(loggedMins)}     Remaining {formatMinutesToHM(remainingMins)}
                    </div>
                  </div>

                  {(group.plannedItems?.length || 0) > 0 && (
                    <div className="mt-2">
                      <div className="text-[11px] font-medium text-gray-700 dark:text-gray-300">Planned</div>
                      <div className="mt-1 space-y-0.5">
                        {group.plannedItems.map((it, idx) => (
                          <div key={`${it.assignmentId || 'a'}:${idx}`} className="flex justify-between gap-2 text-[11px] text-gray-700 dark:text-gray-300">
                            <div className="truncate">{it.label}</div>
                            <div className="tabular-nums whitespace-nowrap">{formatMinutesToHM(it.minutes)}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {(group.loggedItems?.length || 0) > 0 && (
                    <div className="mt-2">
                      <div className="text-[11px] font-medium text-gray-700 dark:text-gray-300">Logged</div>
                      <div className="mt-1 space-y-0.5">
                        {group.loggedItems.map((it, idx) => (
                          <div key={`${it.entryId || 'e'}:${idx}`} className="flex justify-between gap-2 text-[11px] text-gray-700 dark:text-gray-300">
                            <div className="truncate">{it.label}</div>
                            <div className="tabular-nums whitespace-nowrap">{formatMinutesToHM(it.minutes)}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </details>
      <div className="h-2 rounded bg-gray-200 dark:bg-gray-700 relative overflow-hidden">
        <div className="absolute left-0 top-0 h-2 bg-blue-400 dark:bg-blue-500" style={{ width: `${plannedPct}%` }} />
        <div className={`absolute left-0 top-0 h-2 ${utilColor}`} style={{ width: `${loggedPct}%` }} />
      </div>
    </div>
  );
}

function WorkloadDrawer({ open, onClose, user, range, from, to, projectId, projectLabel }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [days, setDays] = useState([]);

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onClose(); }
    if (open) window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!open || !user) return;
      setLoading(true); setError('');
      try {
        const res = await fetchUserWorkload(user.user_id, from, to, projectId || null);
        if (!cancelled && res && Array.isArray(res.days)) setDays(res.days);
      } catch (err) {
        if (!cancelled) setError('Workload data unavailable');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [open, user, from, to, projectId]);

  return (
    <div className={`fixed inset-0 z-40 ${open ? '' : 'pointer-events-none'}`}> 
      {/* Overlay */}
      <div
        onClick={onClose}
        className={`absolute inset-0 bg-black/30 transition-opacity ${open ? 'opacity-100' : 'opacity-0'}`}
      />
      {/* Panel */}
      <div className={`absolute right-0 top-0 h-full w-full sm:w-[420px] bg-white dark:bg-gray-900 shadow-xl transform transition-transform ${open ? 'translate-x-0' : 'translate-x-full'}`}>
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-800">
          <div>
            <div className="text-lg font-semibold">{user?.display_name || 'User'}{projectLabel ? ` — ${projectLabel}` : ''}</div>
            <div className="text-xs text-gray-600 dark:text-gray-400">{range} • {from} – {to}</div>
          </div>
          <button onClick={onClose} aria-label="Close" className="rounded p-2 hover:bg-gray-100 dark:hover:bg-gray-800">✕</button>
        </div>
        <div className="p-4 space-y-3 overflow-y-auto h-[calc(100%-56px)]">
          {loading && <div className="text-sm text-gray-600 dark:text-gray-400">Loading...</div>}
          {error && <div className="text-sm text-red-600">{error}</div>}
          {!loading && !error && days.map(d => (
            <DayCard key={d.date} day={d} userId={user?.user_id} projectId={projectId || null} />
          ))}
          {!loading && !error && days.length === 0 && (
            <div className="text-sm text-gray-600 dark:text-gray-400">No data</div>
          )}
        </div>
      </div>
    </div>
  );
}

function clampDate(ymd, minYmd, maxYmd) {
  const d = new Date(ymd + 'T00:00:00');
  const min = new Date(minYmd + 'T00:00:00');
  const max = new Date(maxYmd + 'T00:00:00');
  if (d < min) return minYmd;
  if (d > max) return maxYmd;
  return ymd;
}

function ProjectsTab({ uiRange, apiRange, from, to, managerId, projectId, managerOptions, onProjectsLoaded, onOpenUserWorkload, onRefresh }) {
  const themeName = resolveThemeName();
  const isWarmFocus = isRedFocusThemeName(themeName);
  const isLightRedPremium = String(themeName) === 'redLightPremium';
  const isRed2 = String(themeName) === 'red2';
  const activeTheme = getActiveTheme();
  const warmTheme = isWarmFocus ? activeTheme : null;
  const accentBorder = isLightRedPremium ? hexToRgba(activeTheme?.primary || '#D72638', 0.42) : null;
  const kpiBoxBorderStyle = isLightRedPremium ? { border: `1px solid ${accentBorder}` } : undefined;
  const nav = useNavigate();
  const { refreshKey: appRefreshKey } = useAppRefresh();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [projects, setProjects] = useState([]);
  const [projectStats, setProjectStats] = useState(new Map());
  const [drawerProj, setDrawerProj] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [me, setMe] = useState({ id: null, role: null });
  const [createOpen, setCreateOpen] = useState(false);
  const [createError, setCreateError] = useState('');
  const [createForm, setCreateForm] = useState({ name: '', client_id: '', start_date: '', end_date: '' });
  const [createClients, setCreateClients] = useState([]); // [{id,name,color}]
  const [createSaving, setCreateSaving] = useState(false);
  // Mini Gantt view: default current quarter
  const [ganttView, setGanttView] = useState(() => {
    return 'year';
  });

  useEffect(() => {
    if (!createOpen) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchManagerClients();
        const rows = Array.isArray(res?.clients) ? res.clients : [];
        if (!cancelled) setCreateClients(rows);
      } catch {
        if (!cancelled) setCreateClients([]);
      }
    })();
    return () => { cancelled = true; };
  }, [createOpen, appRefreshKey]);
  const ganttWindow = useMemo(() => {
    const now = new Date();
    const y = now.getFullYear();
    const toYmd = (d) => {
      const yy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${yy}-${mm}-${dd}`;
    };

    if (String(ganttView) === 'year') {
      const fromDate = new Date(y, 0, 1);
      const toDate = new Date(y, 11, 31);
      return {
        mode: 'year',
        year: y,
        fromYMD: toYmd(fromDate),
        toYMD: toYmd(toDate),
        label: `Year ${y}`,
      };
    }

    const q = Math.max(1, Math.min(4, Number(String(ganttView).replace('q', '')) || 1));
    const startMonth = (q - 1) * 3;
    const fromDate = new Date(y, startMonth, 1);
    const toDate = new Date(y, startMonth + 3, 0);
    return {
      mode: 'quarter',
      year: y,
      quarter: q,
      fromYMD: toYmd(fromDate),
      toYMD: toYmd(toDate),
      label: `Q${q} ${y}`,
    };
  }, [ganttView]);

  const projFrom = ganttWindow.fromYMD;
  const projTo = ganttWindow.toYMD;

  const managerFirstNameByUserId = useMemo(() => {
    const map = new Map();
    (Array.isArray(managerOptions) ? managerOptions : []).forEach(m => {
      if (m?.id == null) return;
      const display = String(m.display_name || '').trim();
      if (!display) return;
      const first = display.split(/\s+/).filter(Boolean)[0] || '';
      if (first) map.set(String(m.id), first);
    });
    return map;
  }, [managerOptions]);

  const projectsWithManagerLabel = useMemo(() => {
    return (Array.isArray(projects) ? projects : []).map(p => {
      const first = p?.created_by_user_id != null
        ? (managerFirstNameByUserId.get(String(p.created_by_user_id)) || '')
        : '';
      return { ...p, manager_first_name: first || null };
    });
  }, [projects, managerFirstNameByUserId]);

  const filteredProjects = useMemo(() => {
    const list = Array.isArray(projectsWithManagerLabel) ? projectsWithManagerLabel : [];
    const byManager = managerId ? list.filter(p => String(p.created_by_user_id || '') === String(managerId)) : list;
    const byProject = projectId != null ? byManager.filter(p => Number(p.id) === Number(projectId)) : byManager;
    return byProject;
  }, [projectsWithManagerLabel, managerId, projectId]);

  // KPI totals across filtered projects
  const kpiTotals = useMemo(() => {
    let planned = 0, logged = 0, assigned = 0, other = 0;
    (Array.isArray(filteredProjects) ? filteredProjects : []).forEach(p => {
      const st = projectStats.get(p.id) || {};
      planned += st.plannedTotal || 0;
      logged += st.loggedTotal || 0;
      assigned += st.assignedTotal || 0;
      other += st.otherTotal || 0;
    });
    const util = planned > 0 ? Math.min(100, Math.round((logged * 100) / planned)) : 0;
    return { planned, logged, assigned, other, util };
  }, [filteredProjects, projectStats]);

  // At-risk projects (overbooked or warning), sorted by severity and utilization
  const atRiskProjects = useMemo(() => {
    const rows = filteredProjects.map(p => ({ p, st: projectStats.get(p.id) || {} }))
      .filter(r => r.st.status === 'overbooked' || r.st.status === 'warning')
      .map(r => ({
        id: r.p.id,
        name: r.p.name,
        client: r.p.client_name,
        status: r.st.status,
        planned: r.st.plannedTotal || 0,
        logged: r.st.loggedTotal || 0,
        util: (r.st.plannedTotal||0) > 0 ? Math.min(150, Math.round((r.st.loggedTotal||0) * 100 / (r.st.plannedTotal||1))) : 0,
      }));
    const sev = s => ({ overbooked: 0, warning: 1 })[s] ?? 2;
    rows.sort((a,b) => {
      const sa = sev(a.status), sb = sev(b.status);
      if (sa !== sb) return sa - sb;
      if (a.util !== b.util) return b.util - a.util;
      return (a.name||'').localeCompare(b.name||'');
    });
    return rows.slice(0, 6);
  }, [filteredProjects, projectStats]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true); setError('');
      try {
        const list = await fetchProjectsList();
        const safeList = Array.isArray(list) ? list : [];
        setProjects(safeList);
        onProjectsLoaded && onProjectsLoaded(safeList);
        // PERF: bulk team-load (one request) instead of N+1 per project
        const projectIds = safeList.length
          ? safeList.map(p => Number(p.id)).filter(n => Number.isFinite(n) && n > 0).join(',')
          : 'all';
        const bulk = await fetchTeamLoad(apiRange, null, { from, to, projectIds, mode: 'summary' });
        const byProject = bulk?.byProject || {};

        const statsEntries = safeList.map(p => {
          const block = byProject[String(p.id)] || byProject[p.id] || null;
          const users = Array.isArray(block?.users)
            ? block.users
            : (Array.isArray(block?.byPerson) ? block.byPerson : []);
          const kpi = block?.kpi || null;

          // Full bulk mode: totals are in kpi/users. Summary mode: totals are at top-level.
          const plannedTotal = kpi
            ? (Number(kpi.planned_minutes_total || 0) || 0)
            : (block && block.planned_minutes_total != null
              ? (Number(block.planned_minutes_total || 0) || 0)
              : users.reduce((acc, u) => acc + (u.planned_minutes_total || 0), 0));
          const loggedTotal = kpi
            ? (Number(kpi.logged_minutes_total || 0) || 0)
            : (block && block.logged_minutes_total != null
              ? (Number(block.logged_minutes_total || 0) || 0)
              : users.reduce((acc, u) => acc + (u.logged_minutes_total || 0), 0));
          const assignedTotal = kpi
            ? (Number(kpi.logged_assigned_minutes_total || 0) || 0)
            : (block && block.logged_assigned_minutes_total != null
              ? (Number(block.logged_assigned_minutes_total || 0) || 0)
              : users.reduce((acc, u) => acc + (u.logged_assigned_minutes_total || 0), 0));
          const otherTotal = kpi
            ? (Number(kpi.logged_other_minutes_total || 0) || 0)
            : (block && block.logged_other_minutes_total != null
              ? (Number(block.logged_other_minutes_total || 0) || 0)
              : users.reduce((acc, u) => acc + (u.logged_other_minutes_total || 0), 0));

          // Derive project status: overbooked > warning > ok > neutral
          const hasOver = users.some(u => u.status === 'overbooked');
          const hasWarn = !hasOver && users.some(u => u.status === 'warning');
          const hasOk = !hasOver && !hasWarn && users.some(u => u.status === 'ok');
          const status = block?.status || (hasOver ? 'overbooked' : hasWarn ? 'warning' : hasOk ? 'ok' : 'neutral');

          const overbookedDaysCount = block && block.overbooked_days_count != null
            ? (Number(block.overbooked_days_count || 0) || 0)
            : 0;

          return [p.id, { plannedTotal, loggedTotal, assignedTotal, otherTotal, status, overbookedDaysCount, teamUsers: users }];
        });

        setProjectStats(new Map(statsEntries));
      } catch (err) {
        if (!cancelled) setError('Workload data unavailable');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [apiRange, from, to, refreshKey]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_BASE_URL}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) {
          const j = await res.json();
          if (!cancelled) setMe({ id: j.user?.id || null, role: j.user?.role || null });
        }
      } catch {}
    })();
    return () => { cancelled = true; };
  }, []);

  const todayYmd = (() => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${dd}`;
  })();

  const [workProject, setWorkProject] = useState(null);
  const [globalWorkOpen, setGlobalWorkOpen] = useState(false);
  const [expandedProjectIds, setExpandedProjectIds] = useState(() => new Set());
  const [doneProjectsCollapsed, setDoneProjectsCollapsed] = useState(true);

  // Portfolio card surface (soft gradient like Hero)
  function PortfolioCard({ proj }) {
    const stats = projectStats.get(proj.id) || { plannedTotal: 0, loggedTotal: 0, assignedTotal: 0, otherTotal: 0, status: 'neutral' };
    const startLabel = proj.start_date ? formatDateDMY(proj.start_date) : '—';
    const endLabel = proj.end_date ? formatDateDMY(proj.end_date) : '—';
    const planned = stats.plannedTotal || 0;
    const logged = stats.loggedTotal || 0;
    const pct = planned > 0 ? Math.min(100, Math.round((logged * 100) / planned)) : 0;
    const red2Border = activeTheme?.primary || '#e11d48';
    const isExpanded = expandedProjectIds.has(proj.id);

    const detailsInnerRef = useRef(null);
    const [detailsMaxHeight, setDetailsMaxHeight] = useState('0px');
    useLayoutEffect(() => {
      const el = detailsInnerRef.current;
      if (!el) return;
      if (!isExpanded) {
        setDetailsMaxHeight('0px');
        return;
      }
      // Measure after layout so we can animate max-height smoothly.
      const next = `${el.scrollHeight || 0}px`;
      setDetailsMaxHeight(next);
    }, [isExpanded, planned, logged, stats.assignedTotal, stats.otherTotal, stats.overbookedDaysCount]);

    return (
      <div
        className="self-start rounded-lg p-3 bg-gradient-to-r from-indigo-50 via-pink-50 to-amber-50 dark:from-slate-800 dark:via-fuchsia-900/30 dark:to-amber-900/30 border border-gray-200 dark:border-gray-800 cursor-pointer"
        style={{
          boxShadow: shadows.sm,
          ...(isWarmFocus
            ? { background: `linear-gradient(90deg, ${warmTheme?.surface2 || '#fff1f2'} 0%, #ffffff 100%)` }
            : null),
          ...(isRed2
            ? {
                background: `linear-gradient(90deg, #ffffff 0%, ${hexToRgba(red2Border, 0.14)} 100%)`,
                borderColor: hexToRgba(red2Border, 0.65),
              }
            : null),
          ...(accentBorder
            ? { borderColor: accentBorder }
            : null),
        }}
        role="button"
        tabIndex={0}
        onClick={() => nav(`/projects/${proj.id}`)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            nav(`/projects/${proj.id}`);
          }
        }}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="text-base font-semibold truncate" style={isRed2 ? { color: red2Border } : undefined}>{proj.name}</div>
            <div className="text-xs text-gray-600 dark:text-gray-400 truncate">{proj.client_name || '—'}</div>
            {proj.manager_first_name ? (
              <div className="text-xs text-gray-600 dark:text-gray-400 truncate">Manager: {proj.manager_first_name}</div>
            ) : null}
          </div>
          <button
            type="button"
            className="shrink-0 px-2 py-1 rounded bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 text-xs"
            aria-label={isExpanded ? 'Collapse details' : 'Expand details'}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setExpandedProjectIds(prev => {
                const next = new Set(prev);
                if (next.has(proj.id)) next.delete(proj.id);
                else next.add(proj.id);
                return next;
              });
            }}
          >
            {isExpanded ? '▲' : '▼'}
          </button>
        </div>
        <div className="mt-1 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div className="text-[12px] text-gray-700 dark:text-gray-300">{startLabel} – {endLabel}</div>
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              className={`px-3 py-1 rounded text-white text-xs ${isWarmFocus ? 'hover:brightness-95' : 'bg-blue-600 hover:bg-blue-700'}`}
              style={isWarmFocus ? { background: warmTheme?.primary || '#e11d48' } : undefined}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                try {
                  setWorkProject(proj);
                } catch (err) {
                  console.error('Assign Work open error', err);
                }
              }}
            >
              Assign Work
            </button>
            <button
              type="button"
              className="px-3 py-1 rounded bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 text-xs"
              onClick={(e) => { e.stopPropagation(); nav('/reports'); }}
            >
              Export
            </button>
          </div>
        </div>

        {/* Expandable details (animated) */}
        <div
          className="mt-3 overflow-hidden transition-[max-height,opacity] duration-200 ease-in-out"
          style={{
            maxHeight: isExpanded ? detailsMaxHeight : '0px',
            opacity: isExpanded ? 1 : 0,
          }}
          aria-hidden={!isExpanded}
        >
          <div ref={detailsInnerRef} className="pt-3 border-t border-gray-200 dark:border-gray-800 text-[12px]">
            <div>
              <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded overflow-hidden">
                <div className="h-2 bg-gray-500" style={{ width: `${pct}%` }} />
              </div>
              <div className="text-[11px] text-gray-600 dark:text-gray-400 mt-1">{planned > 0 ? `${pct}% of planned` : 'No planned'}</div>
            </div>

            <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div>Planned total: {formatMinutesToHM(planned)}</div>
              <div>Logged total: {formatMinutesToHM(logged)}</div>
              <div>Logged (Planned): {formatMinutesToHM(stats.assignedTotal || 0)}</div>
              <div>Logged (Other): {formatMinutesToHM(stats.otherTotal || 0)}</div>
            </div>
            {Number(stats.overbookedDaysCount || 0) > 0 ? (
              <div className="mt-2">Overbooked days: {Number(stats.overbookedDaysCount || 0)}</div>
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  function DoneProjectsSection({ count, children }) {
    return (
      <div className="mt-5">
        <div
          className="rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50/70 dark:bg-gray-900/40 p-3"
          style={{
            boxShadow: shadows.sm,
            ...(accentBorder ? { borderColor: accentBorder } : null),
          }}
        >
          <button
            type="button"
            className="w-full flex items-center justify-between gap-3 text-left"
            onClick={() => setDoneProjectsCollapsed(v => !v)}
            aria-expanded={!doneProjectsCollapsed}
          >
            <div>
              <div className="text-sm font-medium">Done projects</div>
              <div className="text-xs text-gray-600 dark:text-gray-400">{count} {count === 1 ? 'project' : 'projects'}</div>
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
              <span>{doneProjectsCollapsed ? 'Show' : 'Hide'}</span>
              <span aria-hidden>{doneProjectsCollapsed ? '▸' : '▾'}</span>
            </div>
          </button>
          {!doneProjectsCollapsed && (
            <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-800">
              {children}
            </div>
          )}
        </div>
      </div>
    );
  }

  function MiniGantt() {
    const [tip, setTip] = useState({ show: false, text: '', x: 0, y: 0 });
    const [sort, setSort] = useState({ key: 'client', dir: 'asc' });
    const theme = getActiveTheme();
    const ganttBg = theme?.surface || '#ffffff';
    const ganttInset = theme?.surface2 || '#f1f5f9';
    const ganttBorder = theme?.border || '#cbd5e1';
    const ganttText = theme?.text || '#0f172a';
    const ganttMuted = theme?.mutedText || '#64748b';
    const ganttActiveBg = isWarmFocus
      ? hexToRgba(warmTheme?.primary || '#e11d48', 0.18)
      : (isLightRedPremium
        ? hexToRgba(activeTheme?.primary || '#D72638', 0.14)
        : ganttInset);
    const ganttActiveBorder = accentBorder || theme?.primary || '#475569';

    const NAME_COL_W = '8rem';
    const CLIENT_COL_W = '10rem';
    const RANGE_COL_W = '13rem';
    const GRID_COLS = `${NAME_COL_W} ${CLIENT_COL_W} 1fr ${RANGE_COL_W}`;

    // Map date strings to index in selected Gantt window
    const days = useMemo(() => {
      const arr = [];
      const s = new Date(projFrom + 'T00:00:00');
      const e = new Date(projTo + 'T00:00:00');
      for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
        arr.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
      }
      return arr;
    }, [projFrom, projTo]);

    const dayIndex = useMemo(() => {
      const map = new Map();
      for (let i = 0; i < days.length; i++) map.set(days[i], i);
      return map;
    }, [days]);

    const totalDays = Math.max(1, days.length);
    // Convert a day-boundary index (0..days.length) into a percent for positioning within the timeline.
    // We use boundaries so month labels and bars share the exact same scale.
    const toPct = (boundaryIndex) => {
      const denom = Math.max(1, totalDays);
      const b = Math.max(0, Math.min(denom, Number(boundaryIndex) || 0));
      return (b / denom) * 100;
    };
    const idx = (ymd) => {
      const v = dayIndex.get(ymd);
      return v == null ? -1 : v;
    };
    const todayIdx = idx(clampDate(todayYmd, projFrom, projTo));

    const monthSegments = useMemo(() => {
      if (!days.length) return [];
      const starts = [];
      for (let i = 0; i < days.length; i++) {
        const d = new Date(days[i] + 'T00:00:00');
        if (i === 0 || d.getDate() === 1) {
          starts.push({ i, label: d.toLocaleString(undefined, { month: 'short' }) });
        }
      }
      const segs = [];
      for (let si = 0; si < starts.length; si++) {
        const start = starts[si].i;
        const end = (si + 1 < starts.length) ? starts[si + 1].i : days.length;
        segs.push({ start, end, label: starts[si].label });
      }
      return segs;
    }, [days]);
    const weekGridIdxs = useMemo(() => {
      if (ganttWindow.mode !== 'quarter') return [];
      const s = new Date(projFrom + 'T00:00:00');
      const e = new Date(projTo + 'T00:00:00');

      // Find first Monday >= start
      const first = new Date(s);
      const dow = first.getDay(); // 0=Sun..6=Sat
      const add = (8 - dow) % 7; // Sunday->1, Monday->0, Tue->6, ...
      first.setDate(first.getDate() + add);

      const out = [];
      for (let d = new Date(first); d <= e; d.setDate(d.getDate() + 7)) {
        const ymd = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        const i = idx(ymd);
        if (i > 0 && i < days.length) out.push(i);
      }
      return out;
    }, [ganttWindow.mode, projFrom, projTo, days.length, dayIndex]);


    // Helper to parse YMD and clamp to range
    const clampToRange = (ymd) => clampDate(ymd, projFrom, projTo);
    const parseOrNull = (ymd) => {
      if (!ymd) return null;
      const d = new Date(ymd + 'T00:00:00');
      return isNaN(d.getTime()) ? null : ymd;
    };

    // Build per-project timeline using real start/end
    const projDates = filteredProjects.map(p => {
      const s = parseOrNull(p.start_date);
      const e = parseOrNull(p.end_date);
      const label = (s && e) ? `${s} → ${e}` : '—';
      const clientLabel = p.client_name || p.client || '—';
      const barColor = deriveProjectColor(p.client_color, p.id);
      if (!s || !e) {
        return { id: p.id, name: p.name, client: clientLabel, label, outOfRange: true, barColor, sortStart: null };
      }
      // Overlap within selected window
      const overlapStart = clampToRange(s < projFrom ? projFrom : s);
      const overlapEnd = clampToRange(e > projTo ? projTo : e);
      const startI = idx(overlapStart);
      const endI = idx(overlapEnd);
      const outOfRange = (new Date(overlapStart + 'T00:00:00') > new Date(overlapEnd + 'T00:00:00')) || startI < 0 || endI < 0;
      return {
        id: p.id,
        name: p.name,
        client: clientLabel,
        label,
        outOfRange,
        startI: Math.max(0, startI),
        endI: Math.max(0, endI),
        barColor,
        sortStart: s,
      };
    });

    const sortedProjDates = useMemo(() => {
      const list = Array.isArray(projDates) ? projDates.slice() : [];
      const dirMul = sort.dir === 'desc' ? -1 : 1;
      const cmpText = (a, b) => String(a || '').toLowerCase().localeCompare(String(b || '').toLowerCase());
      list.sort((a, b) => {
        if (sort.key === 'client') {
          const c = cmpText(a.client, b.client);
          if (c !== 0) return c * dirMul;
          return cmpText(a.name, b.name) * dirMul;
        }
        if (sort.key === 'date') {
          const aa = a.sortStart || '9999-12-31';
          const bb = b.sortStart || '9999-12-31';
          const c = String(aa).localeCompare(String(bb));
          if (c !== 0) return c * dirMul;
          return cmpText(a.name, b.name) * dirMul;
        }
        // default name
        const c = cmpText(a.name, b.name);
        if (c !== 0) return c * dirMul;
        return cmpText(a.client, b.client) * dirMul;
      });
      return list;
    }, [projDates, sort.key, sort.dir]);

    const toggleSort = (key) => {
      setSort(prev => {
        if (prev.key === key) return { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
        return { key, dir: key === 'date' ? 'asc' : 'asc' };
      });
    };

    const SortHeader = ({ label, keyName, align = 'left' }) => (
      <button
        type="button"
        onClick={() => toggleSort(keyName)}
        className={`w-full h-5 flex items-start text-[11px] font-medium hover:underline ${align === 'right' ? 'justify-end' : 'justify-start'}`}
        style={{ color: ganttMuted }}
        title={`Sort by ${label}`}
      >
        <span className="truncate">
          {label}
          {sort.key === keyName ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
        </span>
      </button>
    );

    return (
      <div
        className="mt-4 p-3 border"
        style={{
          borderRadius: radii.sm,
          boxShadow: shadows.sm,
          background: ganttBg,
          borderColor: accentBorder || ganttBorder,
          color: ganttText,
        }}
      >
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm font-medium">Mini Gantt — {ganttWindow.label}</div>
          <div className="inline-flex items-center gap-1 rounded-lg border p-1" style={{ borderColor: accentBorder || ganttBorder, background: ganttBg }}>
            {[
              { key: 'q1', label: 'Q1' },
              { key: 'q2', label: 'Q2' },
              { key: 'q3', label: 'Q3' },
              { key: 'q4', label: 'Q4' },
              { key: 'year', label: `Year ${ganttWindow.year}` },
            ].map(r => (
              (() => {
                const isActive = String(ganttView) === String(r.key);
                return (
                  <button
                    key={r.key}
                    onClick={() => setGanttView(r.key)}
                    className="px-3 py-1.5 text-xs rounded-md border transition-colors"
                    style={{
                      background: isActive ? ganttActiveBg : 'transparent',
                      borderColor: isActive ? ganttActiveBorder : 'transparent',
                      color: ganttText,
                      fontWeight: isActive ? 600 : 500,
                    }}
                  >
                    {r.label}
                  </button>
                );
              })()
            ))}
          </div>
        </div>

        <div className="overflow-x-auto">
          <div className="min-w-[900px] grid gap-2" style={{ gridTemplateColumns: GRID_COLS }}>
          {/* Project names */}
          <div>
            <div className="mb-2">
              <SortHeader label="Project" keyName="name" />
            </div>
            <div className="space-y-2">
              {sortedProjDates.map(p => (
                <div key={`name:${p.id}`} className="h-4 flex items-center" title={p.name}>
                  <div className="text-sm truncate text-left">{p.name}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Client names */}
          <div>
            <div className="mb-2">
              <SortHeader label="Client" keyName="client" />
            </div>
            <div className="space-y-2">
              {sortedProjDates.map(p => (
                <div key={`client:${p.id}`} className="h-4 flex items-center" title={p.client}>
                  <div className="text-[11px] truncate text-left" style={{ color: ganttMuted }}>{p.client}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Timeline */}
          <div className="relative">
            {/* Month scale (timeline only) */}
            <div className="mb-2 h-5 relative z-10">
              {monthSegments.map((m) => {
                const left = toPct(m.start);
                const right = toPct(m.end);
                const width = Math.max(0, right - left);
                return (
                  <div
                    key={`mo:${m.start}`}
                    className="absolute top-0 bottom-0 flex items-start justify-center overflow-hidden"
                    style={{ left: `${left}%`, width: `${width}%`, color: ganttMuted }}
                  >
                    <span className="text-[11px] whitespace-nowrap">{m.label}</span>
                  </div>
                );
              })}
              <div className="absolute inset-x-0 bottom-0 h-px" style={{ background: ganttBorder }} />
            </div>

            {/* Grid/today overlays */}
            <div className="absolute inset-0 pointer-events-none z-0">
              {ganttWindow.mode === 'quarter' && weekGridIdxs.map((i) => (
                <div
                  key={`wk:${i}`}
                  className="absolute top-0 bottom-0 w-px"
                  style={{ left: `${toPct(i)}%`, background: ganttBorder, opacity: 0.35 }}
                />
              ))}
              {todayIdx >= 0 && (
                <div
                  className="absolute top-0 bottom-0"
                  style={{ left: `${toPct(todayIdx)}%`, borderLeft: `1px solid ${ganttMuted}`, opacity: 0.9 }}
                />
              )}
            </div>

            {/* Bars */}
            <div className="space-y-2 relative z-10">
                {sortedProjDates.map(p => {
                const barClass = 'hover:brightness-110';
                if (p.outOfRange) {
                  return (
                    <div key={`bar:${p.id}`} className="h-4 rounded relative" style={{ background: ganttInset }}>
                      <div className="absolute inset-y-0 left-0 right-0 rounded" style={{ background: ganttBorder, opacity: 0.18 }} />
                      <div className="absolute inset-0 flex items-center justify-center text-[11px]" style={{ color: ganttMuted }}>
                        out of range
                      </div>
                    </div>
                  );
                }

                const leftPct = toPct(p.startI);
                let widthPct = toPct(p.endI + 1) - toPct(p.startI);
                widthPct = Math.max(1.5, widthPct); // min width for visibility

                return (
                  <div key={`bar:${p.id}`} className="h-4 rounded relative" style={{ background: ganttInset }}>
                    <button
                      title={`${p.name} • ${p.client}`}
                      onClick={() => nav(`/projects/${p.id}`)}
                      className={`absolute h-4 rounded ${barClass}`}
                      style={{ left: `${leftPct}%`, width: `${widthPct}%`, background: p.barColor || '#9CA3AF' }}
                      onMouseEnter={(e) => {
                        const overlStart = days[p.startI];
                        const overlEnd = days[p.endI];
                        setTip({ show: true, text: `${p.label} • ${formatDateDMY(overlStart)} → ${formatDateDMY(overlEnd)}`, x: e.clientX, y: e.clientY });
                      }}
                      onMouseMove={(e) => setTip(t => ({ ...t, x: e.clientX, y: e.clientY }))}
                      onMouseLeave={() => setTip({ show: false, text: '', x: 0, y: 0 })}
                    />
                  </div>
                );
              })}
            </div>
          </div>

          {/* Date range labels */}
          <div>
            <div className="mb-2">
              <SortHeader label="Dates" keyName="date" align="right" />
            </div>
            <div className="space-y-2">
              {sortedProjDates.map(p => (
                <div key={`lbl:${p.id}`} className="h-4 flex items-center">
                  <div className="text-[11px] truncate" style={{ color: ganttMuted }}>
                    {p.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
          </div>
        </div>

        {/* Tooltip */}
        {tip.show && (
          <div
            className="fixed z-50 px-2 py-1 rounded bg-black text-white text-[11px] pointer-events-none"
            style={{ left: tip.x + 10, top: tip.y + 10, maxWidth: '50vw' }}
          >
            {tip.text}
          </div>
        )}
      </div>
    );
  }

  function ProjectDrawer() {
    const proj = drawerProj;
    const totalMode = String(uiRange) === 'total';
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [overview, setOverview] = useState(null);
    const [items, setItems] = useState(null);
    const [me, setMe] = useState({ id: null, role: null });
    const [editOpen, setEditOpen] = useState(false);
    const [editError, setEditError] = useState('');
    const [editForm, setEditForm] = useState({ name: '', client_name: '', start_date: '', end_date: '' });
    const [editSaving, setEditSaving] = useState(false);

    useEffect(() => {
      let cancelled = false;
      async function load() {
        if (!proj) return;
        setLoading(true); setError(''); setOverview(null); setItems(null);
        try {
          if (totalMode) {
            const st = projectStats.get(proj.id) || {};
            const plannedTotal = st.plannedTotal || 0;
            const loggedTotal = st.loggedTotal || 0;
            const assignedTotal = st.assignedTotal || 0;
            const otherTotal = st.otherTotal || 0;
            const teamUsers = Array.isArray(st.teamUsers) ? st.teamUsers : [];
            const ov = {
              from,
              to,
              kpi: {
                planned_minutes_total: plannedTotal,
                logged_minutes_total: loggedTotal,
                logged_assigned_minutes_total: assignedTotal,
                logged_other_minutes_total: otherTotal,
                overbooked_days_count: 0,
              },
              team: teamUsers,
            };
            if (!cancelled) setOverview(ov);
          } else {
            const ov = await fetchProjectOverview(proj.id, apiRange);
            if (!cancelled) setOverview(ov);
          }
        } catch (err) {
          if (!cancelled) setError('Project data unavailable');
        } finally {
          if (!cancelled) setLoading(false);
        }
      }
      load();
      return () => { cancelled = true; };
    }, [proj, totalMode, apiRange, from, to, projectStats]);

    useEffect(() => {
      let cancelled = false;
      (async () => {
        try {
          const token = localStorage.getItem('token');
          const res = await fetch(`${API_BASE_URL}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
          if (res.ok) {
            const j = await res.json();
            if (!cancelled) setMe({ id: j.user?.id || null, role: j.user?.role || null });
          }
        } catch {}
      })();
      return () => { cancelled = true; };
    }, []);

    if (!proj) return null;
    const projLabel = `${proj.name} (${proj.client_name || '—'})`;

    return (
      <div className="fixed inset-0 z-40">
        <div onClick={() => setDrawerProj(null)} className="absolute inset-0 bg-black/30" />
        <div className="absolute right-0 top-0 h-full w-full sm:w-[520px] bg-white dark:bg-gray-900 shadow-xl flex flex-col">
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-800">
            <div>
              <div className="text-lg font-semibold">{proj.name}</div>
              <div className="text-xs text-gray-600 dark:text-gray-400">{proj.client_name || '—'} • {formatRangeDMY(from, to)} • {totalMode ? 'Total' : apiRange}</div>
            </div>
            <button onClick={() => setDrawerProj(null)} className="rounded p-2 hover:bg-gray-100 dark:hover:bg-gray-800">✕</button>
          </div>

          <div className="p-4 space-y-4 overflow-y-auto">
            {loading && <div className="text-sm text-gray-600 dark:text-gray-400">Loading...</div>}
            {error && <div className="text-sm text-red-600">{error}</div>}

            {overview && (
              <>
                <div className="flex items-center justify-between">
                  <AddWorkButton project={proj} range={apiRange} from={from} to={to} onCreated={async ()=>{
                  if (!totalMode) {
                    try { const ov = await fetchProjectOverview(proj.id, apiRange); setOverview(ov); } catch {}
                  }
                  setRefreshKey(k=>k+1);
                  onRefresh && onRefresh();
                  }} overview={overview} />
                  {me.role === 'manager' && (
                    proj.created_by_user_id === me.id ? (
                      <button
                        className="px-3 py-1 rounded bg-gray-200 dark:bg-gray-800 text-sm hover:bg-gray-300"
                        onClick={() => { setEditError(''); setEditForm({ name: proj.name || '', client_name: proj.client_name || '', start_date: proj.start_date || '', end_date: proj.end_date || '' }); setEditOpen(true); }}
                      >Edit</button>
                    ) : (
                      <button
                        className="px-3 py-1 rounded bg-gray-200 dark:bg-gray-800 text-sm opacity-60 cursor-not-allowed"
                        title="Only the project creator can edit this project."
                        disabled
                      >Edit</button>
                    )
                  )}
                </div>
                {/* KPI tiles */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="p-3 text-center bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700" style={{ borderRadius: radii.sm, boxShadow: shadows.sm }}>
                    <div className="text-xs text-gray-600 dark:text-gray-400">Planned</div>
                    <div className="font-semibold">{formatMinutesToHM(overview.kpi?.planned_minutes_total || 0)}</div>
                  </div>
                  <div className="p-3 text-center bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700" style={{ borderRadius: radii.sm, boxShadow: shadows.sm }}>
                    <div className="text-xs text-gray-600 dark:text-gray-400">Logged (Total)</div>
                    <div className="font-semibold">{formatMinutesToHM(overview.kpi?.logged_minutes_total || 0)}</div>
                    <div className="text-[11px] text-gray-600 dark:text-gray-400 mt-1">Planned {formatMinutesToHM(overview.kpi?.logged_assigned_minutes_total || 0)} • Other {formatMinutesToHM(overview.kpi?.logged_other_minutes_total || 0)}</div>
                  </div>
                  <div className="p-3 text-center bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700" style={{ borderRadius: radii.sm, boxShadow: shadows.sm }}>
                    <div className="text-xs text-gray-600 dark:text-gray-400">Overbooked days</div>
                    <div className="font-semibold">{overview.kpi?.overbooked_days_count || 0}</div>
                  </div>
                </div>

                {/* Team chips */}
                <div>
                  <div className="text-sm font-medium mb-2">Team</div>
                  <div className="flex flex-wrap gap-2">
                    {(overview.team || []).slice().sort((a,b)=>{
                      const sev = (s) => ({ overbooked:0, warning:1, ok:2, neutral:3 })[s] ?? 3;
                      const sa = sev(a.status), sb = sev(b.status);
                      if (sa !== sb) return sa - sb;
                      const pa = a.planned_minutes_total || 0, pb = b.planned_minutes_total || 0;
                      if (pa !== pb) return pb - pa; // desc
                      const na = (a.display_name||'').toLowerCase();
                      const nb = (b.display_name||'').toLowerCase();
                      return na.localeCompare(nb);
                    }).map(t => {
                      const bg = STATUS_COLORS[t.status] || STATUS_COLORS.neutral;
                      return (
                        <button key={t.user_id}
                          onClick={() => onOpenUserWorkload && onOpenUserWorkload(
                            { user_id: t.user_id, display_name: t.display_name },
                            { id: proj.id, label: `${proj.name} (${proj.client})` }
                          )}
                          className="px-3 py-1 rounded-full text-white text-xs hover:brightness-110 flex items-center gap-2"
                          style={{ background: bg }}
                          title={`Planned ${formatMinutesToHM(t.planned_minutes_total||0)} • Logged ${formatMinutesToHM(t.logged_minutes_total||0)} (Planned ${formatMinutesToHM(t.logged_assigned_minutes_total||0)} • Other ${formatMinutesToHM(t.logged_other_minutes_total||0)})`}
                        >
                          <span>{t.display_name}</span>
                          <span className="text-[10px] opacity-80">Planned {formatMinutesToHM(t.planned_minutes_total||0)}</span>
                        </button>
                      );
                    })}
                    {(overview.team || []).length === 0 && (
                      <div className="text-xs text-gray-600 dark:text-gray-400">No team for this range</div>
                    )}
                  </div>
                </div>

                {/* Work items accordion (optional) */}
                <details onToggle={async (e) => {
                  if (e.currentTarget.open && !items) {
                    try { const wi = await fetchProjectWorkItems(proj.id, overview.from, overview.to); setItems(wi); } catch (_) {}
                  }
                }}>
                  <summary className="text-sm cursor-pointer">Work items</summary>
                  <div className="mt-2 space-y-2">
                    {items && (items.items || []).map((it, idx) => (
                      <div key={idx} className="border border-gray-200 dark:border-gray-700 p-2" style={{ borderRadius: radii.sm, boxShadow: shadows.sm }}>
                        <div className="text-xs text-gray-600 dark:text-gray-400">{it.job?.name || 'Job'} {it.task ? `› ${it.task.name}` : ''}</div>
                        <div className="text-xs">Planned {formatMinutesToHM(it.planned_minutes_total||0)} • Logged {formatMinutesToHM(it.logged_minutes_total||0)}</div>
                      </div>
                    ))}
                    {items && (items.items || []).length === 0 && (
                      <div className="text-xs text-gray-600 dark:text-gray-400">No work items in this range</div>
                    )}
                    {!items && <div className="text-xs text-gray-600 dark:text-gray-400">Loading…</div>}
                  </div>
                </details>
              </>
            )}
            {editOpen && (
              <div className="border rounded p-3 bg-white dark:bg-gray-900">
                <div className="text-sm font-medium mb-2">Edit Project</div>
                {editError && <div className="text-xs text-red-600 mb-2">{editError}</div>}
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <label className="flex flex-col">
                    <span className="mb-1">Name (optional)</span>
                    <input value={editForm.name} onChange={e=>setEditForm(f=>({ ...f, name: e.target.value }))} className="border rounded px-2 py-1 bg-white dark:bg-gray-800" />
                  </label>
                  <label className="flex flex-col">
                    <span className="mb-1">Client name</span>
                    <input value={editForm.client_name} onChange={e=>setEditForm(f=>({ ...f, client_name: e.target.value }))} className="border rounded px-2 py-1 bg-white dark:bg-gray-800" />
                  </label>
                  <label className="flex flex-col">
                    <span className="mb-1">Start date</span>
                    <input type="date" value={editForm.start_date || ''} onChange={e=>setEditForm(f=>({ ...f, start_date: e.target.value }))} className="border rounded px-2 py-1 bg-white dark:bg-gray-800" />
                  </label>
                  <label className="flex flex-col">
                    <span className="mb-1">End date</span>
                    <input type="date" value={editForm.end_date || ''} onChange={e=>setEditForm(f=>({ ...f, end_date: e.target.value }))} className="border rounded px-2 py-1 bg-white dark:bg-gray-800" />
                  </label>
                </div>
                <div className="mt-3 flex items-center justify-end gap-2">
                  <button className="px-3 py-1 rounded bg-gray-200 dark:bg-gray-700" onClick={()=>setEditOpen(false)}>Cancel</button>
                  <button className="px-3 py-1 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60" disabled={editSaving} onClick={async ()=>{
                    setEditError(''); setEditSaving(true);
                    try {
                      const payload = {
                        client_name: editForm.client_name,
                        start_date: editForm.start_date || null,
                        end_date: editForm.end_date || null,
                      };
                      if ((editForm.name||'').trim()) payload.name = editForm.name.trim();
                      const res = await updateProject(proj.id, payload);
                      // Update local drawer project and refresh list/gantt (preserve creator)
                      setDrawerProj(d => d ? { ...d, name: res.name, client_name: res.client_name, start_date: res.start_date?.slice(0,10), end_date: res.end_date?.slice(0,10), created_by_user_id: d.created_by_user_id } : d);
                      setEditOpen(false);
                      // Refresh projects list
                      try { const list = await fetchProjectsList(); setProjects(Array.isArray(list)?list:[]); } catch {}
                      setRefreshKey(k=>k+1);
                    } catch (err) {
                      const msg = String(err?.message || 'Failed to update project');
                      if (/Only the project creator can edit this project\./i.test(msg)) setEditError('Only the project creator can edit this project.'); else setEditError(msg);
                    } finally {
                      setEditSaving(false);
                    }
                  }}>{editSaving ? 'Saving…' : 'Save'}</button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  function AddWorkButton({ project, range, from, to, onCreated, overview }) {
    const [open, setOpen] = useState(false);
    return (
      <div className="flex items-center justify-end">
        <button
          onClick={()=>setOpen(true)}
          className={`px-3 py-1 rounded text-white text-sm ${isWarmFocus ? 'hover:brightness-95' : 'bg-blue-600 hover:bg-blue-700'}`}
          style={isWarmFocus ? { background: warmTheme?.primary || '#e11d48' } : undefined}
        >+ Add work</button>
        {open && (
          <SharedAddWorkModal
            project={project}
            rangeKey={range}
            onClose={() => setOpen(false)}
            onCreated={async () => {
              setOpen(false);
              onCreated && onCreated();
            }}
            overview={overview}
          />
        )}
      </div>
    );
  }

  const { activeProjects, doneProjects } = useMemo(() => {
    return filteredProjects.reduce((acc, proj) => {
      if (isDoneProject(proj, todayYmd)) acc.doneProjects.push(proj);
      else acc.activeProjects.push(proj);
      return acc;
    }, { activeProjects: [], doneProjects: [] });
  }, [filteredProjects, todayYmd]);

  return (
    <div>
      {loading && <div className="text-sm text-gray-600 dark:text-gray-400">Loading...</div>}
      {error && <div className="text-sm text-red-600">{error}</div>}
      {!loading && !error && (
        <div className="space-y-3">
          {/* Hero panel */}
          <div
            className="rounded-lg p-4 border border-gray-200 dark:border-gray-800 bg-gradient-to-r from-indigo-100 via-pink-100 to-amber-100 dark:from-slate-800 dark:via-fuchsia-900/40 dark:to-amber-900/40"
            style={{
              ...(isWarmFocus
                ? { background: `linear-gradient(90deg, ${warmTheme?.surface2 || '#fff1f2'} 0%, #ffffff 100%)` }
                : null),
              ...(isLightRedPremium
                ? { borderColor: accentBorder }
                : null),
            }}
          >
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-base font-semibold">Overview</div>
                <div className="text-xs text-gray-600 dark:text-gray-400">{formatRangeDMY(new Date(from+'T00:00:00'), new Date(to+'T00:00:00'))}</div>
              </div>
              <div className="hidden sm:flex items-center gap-2">
                {me?.role === 'manager' && (
                  <button
                    className={`px-3 py-1 rounded text-white text-sm ${isWarmFocus ? 'hover:brightness-95' : 'bg-blue-600 hover:bg-blue-700'}`}
                    style={isWarmFocus ? { background: warmTheme?.primary || '#e11d48' } : undefined}
                    onClick={()=>{ setCreateError(''); setCreateForm({ name:'', client_id:'', start_date:'', end_date:'' }); setCreateOpen(true); }}
                  >+ New Project</button>
                )}
                <button
                  className={`px-3 py-1 rounded text-white text-sm ${isWarmFocus ? 'hover:brightness-95' : 'bg-gray-900 hover:bg-gray-800'}`}
                  style={isWarmFocus ? { background: warmTheme?.primary || '#e11d48' } : undefined}
                  onClick={() => setGlobalWorkOpen(true)}
                >Assign Work</button>
                <button className="px-3 py-1 rounded bg-gray-200 dark:bg-gray-700 text-sm hover:bg-gray-300" onClick={()=> nav('/reports') }>Export</button>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              <div className="relative p-3 text-center bg-white/70 dark:bg-black/20 rounded" style={kpiBoxBorderStyle}>
                <span
                  className="absolute top-2 right-2 inline-flex items-center justify-center w-4 h-4 rounded-full border border-gray-300 dark:border-gray-600 text-[10px] leading-none text-gray-700 dark:text-gray-200 bg-white/60 dark:bg-black/20 cursor-help select-none"
                  title="Total planned hours in selected range."
                  aria-label="Total planned hours in selected range."
                  tabIndex={0}
                >
                  i
                </span>
                <div className="text-xs text-gray-600 dark:text-gray-400">Planned</div>
                <div className="font-semibold">{formatMinutesToHM(kpiTotals.planned)}</div>
              </div>
              <div className="relative p-3 text-center bg-white/70 dark:bg-black/20 rounded" style={kpiBoxBorderStyle}>
                <span
                  className="absolute top-2 right-2 inline-flex items-center justify-center w-4 h-4 rounded-full border border-gray-300 dark:border-gray-600 text-[10px] leading-none text-gray-700 dark:text-gray-200 bg-white/60 dark:bg-black/20 cursor-help select-none"
                  title="Logged hours that match planned assignments."
                  aria-label="Logged hours that match planned assignments."
                  tabIndex={0}
                >
                  i
                </span>
                <div className="text-xs text-gray-600 dark:text-gray-400">Logged (Planned)</div>
                <div className="font-semibold">{formatMinutesToHM(kpiTotals.assigned)}</div>
              </div>
              <div className="relative p-3 text-center bg-white/70 dark:bg-black/20 rounded" style={kpiBoxBorderStyle}>
                <span
                  className="absolute top-2 right-2 inline-flex items-center justify-center w-4 h-4 rounded-full border border-gray-300 dark:border-gray-600 text-[10px] leading-none text-gray-700 dark:text-gray-200 bg-white/60 dark:bg-black/20 cursor-help select-none"
                  title="Logged hours without planned assignment (unassigned/other)."
                  aria-label="Logged hours without planned assignment (unassigned/other)."
                  tabIndex={0}
                >
                  i
                </span>
                <div className="text-xs text-gray-600 dark:text-gray-400">Logged (Other)</div>
                <div className="font-semibold">{formatMinutesToHM(kpiTotals.other)}</div>
              </div>
              <div className="relative p-3 text-center bg-white/70 dark:bg-black/20 rounded" style={kpiBoxBorderStyle}>
                <span
                  className="absolute top-2 right-2 inline-flex items-center justify-center w-4 h-4 rounded-full border border-gray-300 dark:border-gray-600 text-[10px] leading-none text-gray-700 dark:text-gray-200 bg-white/60 dark:bg-black/20 cursor-help select-none"
                  title="All logged hours in range (planned + other)."
                  aria-label="All logged hours in range (planned + other)."
                  tabIndex={0}
                >
                  i
                </span>
                <div className="text-xs text-gray-600 dark:text-gray-400">Logged (Total)</div>
                <div className="font-semibold">{formatMinutesToHM(kpiTotals.logged)}</div>
              </div>
              <div className="p-3 text-center bg-white/70 dark:bg-black/20 rounded" style={kpiBoxBorderStyle}>
                <div className="text-xs text-gray-600 dark:text-gray-400">Utilization</div>
                <div className="font-semibold">{kpiTotals.util}%</div>
              </div>
            </div>
            {SHOW_RISK_UI && (
              <>
                {/* At-risk list */}
                <div className="mt-3">
                  <div className="text-sm font-medium mb-1">At risk</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {atRiskProjects.map(r => (
                      <button key={r.id} onClick={()=> nav(`/projects/${r.id}`)}
                              className="flex items-center justify-between px-3 py-2 rounded bg-white/70 dark:bg-black/20 hover:bg-white/90 dark:hover:bg-black/30">
                        <div className="truncate">
                          <div className="text-sm font-medium truncate">{r.name}</div>
                          <div className="text-[11px] text-gray-600 dark:text-gray-400 truncate">{r.client || '—'}</div>
                        </div>
                        <div className="text-right">
                          <div className="text-xs">{r.util}%</div>
                          <span className="text-[10px] px-2 py-0.5 rounded text-white" style={{ background: (r.status==='overbooked'?'#8C5A5A':'#9A8F6A') }}>{r.status==='overbooked'?'Overbooked':'At risk'}</span>
                        </div>
                      </button>
                    ))}
                    {atRiskProjects.length===0 && (
                      <div className="text-xs text-gray-700 dark:text-gray-300">No at-risk projects</div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
          <div className="flex items-center justify-end">
            {me.role === 'manager' && (
              <button
                className={`px-3 py-1 rounded text-sm ${isWarmFocus ? 'text-white hover:brightness-95' : 'bg-gray-200 dark:bg-gray-800 hover:bg-gray-300'}`}
                style={isWarmFocus ? { background: warmTheme?.primary || '#e11d48' } : undefined}
                onClick={()=>{ setCreateError(''); setCreateForm({ name:'', client_id:'', start_date:'', end_date:'' }); setCreateOpen(true); }}
              >+ New Project</button>
            )}
          </div>
          {/* Project Portfolio */}
          <div className="mt-2">
            <div className="text-sm font-medium mb-2">Project Portfolio</div>
            {activeProjects.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-start">
                {activeProjects.map(p => <PortfolioCard key={p.id} proj={p} />)}
              </div>
            )}
            {doneProjects.length > 0 && (
              <DoneProjectsSection count={doneProjects.length}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-start">
                  {doneProjects.map(p => <PortfolioCard key={p.id} proj={p} />)}
                </div>
              </DoneProjectsSection>
            )}
            {activeProjects.length === 0 && doneProjects.length === 0 && <div className="text-sm text-gray-600 dark:text-gray-400">No projects</div>}
          </div>
          <MiniGantt />
          <ProjectDrawer />
          {globalWorkOpen && (
            <SharedAddWorkModal
              rangeKey={apiRange}
              onClose={() => setGlobalWorkOpen(false)}
              onCreated={() => {
                setGlobalWorkOpen(false);
                setRefreshKey(k => k + 1);
                onRefresh && onRefresh();
              }}
              overview={{ team: [] }}
            />
          )}
          {workProject && (
            <SharedAddWorkModal
              project={workProject}
              rangeKey={apiRange}
              onClose={() => setWorkProject(null)}
              onCreated={() => {
                setWorkProject(null);
                setRefreshKey(k => k + 1);
                onRefresh && onRefresh();
              }}
              overview={{ team: [] }}
            />
          )}
          {createOpen && (
            <div className="fixed inset-0 z-50">
              <div className="absolute inset-0 bg-black/40" onClick={()=>setCreateOpen(false)} />
              <div className="absolute right-1/2 translate-x-1/2 top-16 w-[520px] max-w-[96vw] bg-white dark:bg-gray-900 rounded shadow-lg p-4">
                <div className="text-lg font-semibold mb-2">Create Project</div>
                {createError && <div className="text-sm text-red-600 mb-2">{createError}</div>}
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <label className="flex flex-col">
                    <span className="mb-1">Name</span>
                    <input value={createForm.name} onChange={e=>setCreateForm(f=>({ ...f, name: e.target.value }))} className="border rounded px-2 py-1 bg-white dark:bg-gray-800" />
                  </label>
                  <label className="flex flex-col">
                    <span className="mb-1">Client</span>
                    <select value={createForm.client_id || ''} onChange={e=>setCreateForm(f=>({ ...f, client_id: e.target.value }))} className="border rounded px-2 py-1 bg-white dark:bg-gray-800">
                      <option value="">Select client</option>
                      {(Array.isArray(createClients) ? createClients : []).map(c => (
                        <option key={c.id} value={String(c.id)}>{c.name}</option>
                      ))}
                    </select>
                  </label>
                  <label className="flex flex-col">
                    <span className="mb-1">Start date</span>
                    <input type="date" value={createForm.start_date || ''} onChange={e=>setCreateForm(f=>({ ...f, start_date: e.target.value }))} className="border rounded px-2 py-1 bg-white dark:bg-gray-800" />
                  </label>
                  <label className="flex flex-col">
                    <span className="mb-1">End date</span>
                    <input type="date" value={createForm.end_date || ''} onChange={e=>setCreateForm(f=>({ ...f, end_date: e.target.value }))} className="border rounded px-2 py-1 bg-white dark:bg-gray-800" />
                  </label>
                </div>
                <div className="mt-3 flex items-center justify-end gap-2">
                  <button className="px-3 py-1 rounded bg-gray-200 dark:bg-gray-700" onClick={()=>setCreateOpen(false)}>Cancel</button>
                  <button className="px-3 py-1 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60" disabled={createSaving} onClick={async ()=>{
                    setCreateError('');
                    const n = (createForm.name||'').trim();
                    if (!n) { setCreateError('Name is required'); return; }
                    if (!createForm.client_id) { setCreateError('Client is required'); return; }
                    const clientIdNum = Number(createForm.client_id);
                    if (!Number.isFinite(clientIdNum)) { setCreateError('Client is invalid'); return; }
                    if (createForm.start_date && createForm.end_date && (new Date(createForm.start_date) > new Date(createForm.end_date))) {
                      setCreateError('start_date must be <= end_date'); return;
                    }
                    setCreateSaving(true);
                    try {
                      const res = await createProject({ name: n, client_id: clientIdNum, start_date: createForm.start_date || undefined, end_date: createForm.end_date || undefined });
                      setCreateOpen(false);
                      // Refresh list and open drawer for the new project
                      try { const list = await fetchProjectsList(); setProjects(Array.isArray(list)?list:[]); } catch {}
                      const created = { id: res.id, name: res.name, client_name: res.client_name, start_date: (res.start_date||'').slice(0,10), end_date: (res.end_date||'').slice(0,10), created_by_user_id: res.created_by_user_id };
                      setDrawerProj(created);
                      setRefreshKey(k=>k+1);
                    } catch (err) {
                      setCreateError(err?.message || 'Failed to create project');
                    } finally {
                      setCreateSaving(false);
                    }
                  }}>{createSaving ? 'Creating…' : 'Create'}</button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function ManagerDashboard() {
  const navigate = useNavigate();
  const [range, setRange] = useState('this_week'); // UI range (can be 'total')
  const [dataRange, setDataRange] = useState('this_week'); // API-supported range key
  const [projectId, setProjectId] = useState(null);
  const [managerId, setManagerId] = useState(() => {
    try { return localStorage.getItem('managerDash.managerId') || ''; } catch { return ''; }
  });
  const [managerOptions, setManagerOptions] = useState([]); // [{id, display_name}]
  const [projectsLite, setProjectsLite] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [calendarWindow, setCalendarWindow] = useState(null); // { from: 'YYYY-MM-DD', to: 'YYYY-MM-DD' }
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [projectForWorkload, setProjectForWorkload] = useState(null);
  const [tab, setTab] = useState('projects');

  const isTotal = String(range) === 'total';
  const apiRange = isTotal ? dataRange : range;

  const todayYmd = useMemo(() => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${dd}`;
  }, []);

  const baseWindow = useMemo(() => getRangeWindow(dataRange, new Date()), [dataRange]);
  const window = useMemo(() => {
    if (!isTotal) return getRangeWindow(range, new Date());
    // Total: expand date window based on filtered projects when possible.
    const list = Array.isArray(projectsLite) ? projectsLite : [];
    const scoped = list.filter(p => {
      if (projectId != null && Number(p.id) !== Number(projectId)) return false;
      if (managerId && String(p.created_by_user_id || '') !== String(managerId)) return false;
      return true;
    });

    const validStart = scoped
      .map(p => (p.start_date || '').slice(0, 10))
      .filter(s => /^\d{4}-\d{2}-\d{2}$/.test(String(s)));
    const validEnd = scoped
      .map(p => (p.end_date || '').slice(0, 10))
      .filter(s => /^\d{4}-\d{2}-\d{2}$/.test(String(s)));

    const fallbackFrom = baseWindow.fromYMD;
    const fallbackTo = baseWindow.toYMD;

    const from = validStart.length ? validStart.slice().sort()[0] : fallbackFrom;
    const maxEnd = validEnd.length ? validEnd.slice().sort().slice(-1)[0] : null;
    // Extend to max(project end, today)
    const to = [maxEnd, todayYmd, fallbackTo].filter(Boolean).slice().sort().slice(-1)[0];
    return {
      fromDate: new Date(from + 'T00:00:00'),
      toDate: new Date(to + 'T00:00:00'),
      fromYMD: from,
      toYMD: to,
      label: 'Total',
    };
  }, [isTotal, range, projectsLite, managerId, projectId, baseWindow.fromYMD, baseWindow.toYMD, todayYmd]);

  const from = window.fromYMD;
  const to = window.toYMD;
  const windowLabel = window.label;

  const headerSubtitle = useMemo(() => {
    if (tab === 'calendar') {
      const cf = calendarWindow?.from;
      const ct = calendarWindow?.to;
      if (cf && ct) {
        return formatRangeDMY(new Date(cf + 'T00:00:00'), new Date(ct + 'T00:00:00'));
      }
    }
    return windowLabel || (from && to ? `${formatRangeDMY(new Date(from + 'T00:00:00'), new Date(to + 'T00:00:00'))}` : '');
  }, [tab, calendarWindow?.from, calendarWindow?.to, windowLabel, from, to]);

  const onRangeChange = (nextKey) => {
    const k = String(nextKey || '');
    setRange(k);
    if (k && k !== 'total') setDataRange(k);
  };

  useEffect(() => {
    try { localStorage.setItem('managerDash.managerId', String(managerId || '')); } catch {}
  }, [managerId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const lk = await fetchPlanningLookups({ redirectOn401: false });
        const opts = (Array.isArray(lk?.managers) ? lk.managers : [])
          .slice()
          .sort((a, b) => String(a.display_name || '').localeCompare(String(b.display_name || '')));
        if (!cancelled) setManagerOptions(opts);
      } catch {
        if (!cancelled) setManagerOptions([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="p-4">
      <SectionHeader
        title="Manager Dashboard"
        subtitle={headerSubtitle}
        actions={tab === 'calendar' ? null : (
          <>
            <SegmentedControl ariaLabel="Range" options={[{key:'last_week',label:'Last week'},{key:'this_week',label:'This week'},{key:'next_week',label:'Next week'},{key:'last_month',label:'Last month'},{key:'this_month',label:'This month'},{key:'total',label:'Total'}]} value={range} onChange={onRangeChange} />
            <ManagerFilter value={managerId} options={managerOptions} onChange={setManagerId} />
            <ProjectFilter projects={projectsLite} managerId={managerId} projectId={projectId} onChange={setProjectId} />
          </>
        )}
        className="mb-4"
      />

      {loading && (
        <div className="text-sm text-gray-600 dark:text-gray-400">Loading...</div>
      )}
      {error && (
        <div className="text-sm text-red-600">{error}</div>
      )}

      {/* Tabs */}
      <div className="mb-4">
        <SegmentedControl ariaLabel="Dashboard tabs" ariaRole="tablist" options={[{key:'projects',label:'Projects'},{key:'calendar',label:'Calendar'}]} value={tab} onChange={setTab} />
      </div>

      {tab === 'projects' && (
        <ProjectsTab
          uiRange={range}
          apiRange={apiRange}
          from={from}
          to={to}
          managerId={managerId}
          projectId={projectId}
          managerOptions={managerOptions}
          onProjectsLoaded={setProjectsLite}
          onOpenUserWorkload={(user, project) => { setSelectedUser(user); setProjectForWorkload(project); }}
          onRefresh={() => setRefreshKey(k => k + 1)}
        />
      )}

      {tab === 'calendar' && (
        <div className="mt-2">
          <Calendar2
            today={new Date()}
            storageNamespace="managerDash"
            enableAssignWorkCta
            onWorkCreated={() => setRefreshKey(k => k + 1)}
            onWindowChange={setCalendarWindow}
          />
        </div>
      )}

      <WorkloadDrawer
        open={!!selectedUser}
        onClose={() => setSelectedUser(null)}
        user={selectedUser}
        range={range}
        from={from}
        to={to}
        projectId={projectForWorkload ? projectForWorkload.id : projectId}
        projectLabel={projectForWorkload ? projectForWorkload.label : undefined}
      />
    </div>
  );
}

// CalendarTab and related internal calendar views have been removed.
